import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { LoginManager } from '../../core/login-manager';
import { refId, refLabel, nomeUtente, iniziali } from '../../lib/format';
import { etichettaLivello } from '../../lib/ruoli';
import {
    useEntityItem, statoLista, SectionHead, Field, DetailActions, messaggioErrore
} from '../admin/common';

/**
 * Area personale: qui il condomino aggiorna da se' i propri recapiti e la
 * password, senza passare dall'amministratore.
 *
 * Il salvataggio non usa `DataManager`: sull'entita' `user` il condomino ha
 * il permesso di lettura sul proprio record ma non quello di scrittura, e un
 * `post/user` gli tornerebbe negato. Le rotte `/account/*` esistono apposta,
 * lavorano sempre e solo sull'utente della sessione.
 *
 * Palazzina, appartamento e ruolo restano in sola lettura: dicono in che casa
 * si abita e cosa si puo' fare, e sono decisioni dell'amministratore, non
 * dell'interessato. Sono qui perche' l'utente deve poterli verificare e, se
 * sbagliati, saperli segnalare.
 *
 * Casa e appartamento compaiono pero' solo se assegnati. Chi amministra il
 * condominio - a maggior ragione chi lo fa per tutti - un appartamento non
 * ce l'ha, e due "Non assegnato" in fila accanto al proprio nome sembrano
 * un profilo incompleto invece di una condizione normale. Il ruolo resta
 * sempre, che c'e' per chiunque.
 *
 * Stanno nella fascia d'intestazione, accanto a nome e email, e non in una
 * scheda per conto loro: erano una tabella con una riga sola, cioe' tre
 * valori che si prendevano l'ingombro di un elenco. Li' dentro dicono la
 * stessa cosa - chi sei e dove stai - in una riga invece che in un blocco.
 *
 * Sotto, i due form vanno affiancati e non impilati: sono brevi, indipendenti
 * e si guardano una volta sola. In colonna la pagina era tre schede uguali
 * una sull'altra, senza un punto da cui cominciare a leggere.
 */
export default function Profile() {
    const utente = LoginManager.getUserInfo();
    const { item, loading, error, reload } = useEntityItem('user', utente ? utente.id : null);

    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    const [password, setPassword] = useState({ attuale: '', nuova: '', conferma: '' });
    const [savingPassword, setSavingPassword] = useState(false);

    useEffect(() => {
        if (!item) return;
        setForm({
            name: item.name || '',
            surname: item.surname || '',
            email: item.email || '',
            telefono: item.telefono || ''
        });
    }, [item]);

    function aggiorna(campo, valore) {
        setForm(prev => ({ ...prev, [campo]: valore }));
    }

    function aggiornaPassword(campo, valore) {
        setPassword(prev => ({ ...prev, [campo]: valore }));
    }

    async function salvaDati(e) {
        e.preventDefault();
        if (!form.name.trim() || !form.surname.trim() || !form.email.trim()) {
            toast.error('Nome, cognome ed email sono obbligatori.', { theme: 'colored' });
            return;
        }

        setSaving(true);
        try {
            /* Solo i recapiti: livello, stato, palazzina e appartamento li
               decide l'amministratore. Non e' questa lista a garantirlo -
               `updateProfile` accetta solo i campi in `PROFILE_FIELDS` e
               ignora il resto - ma mandare qui altro non servirebbe. */
            const email = form.email.trim();
            const res = await LoginManager.updateProfile({
                name: form.name.trim(),
                surname: form.surname.trim(),
                email,
                // username ed email sono la stessa cosa, e il login accetta
                // l'uno o l'altra: senza riscriverlo, cambiando l'email si
                // continuerebbe a entrare anche con la vecchia.
                username: email,
                telefono: form.telefono.trim()
            });

            if (res && res.success === 1) {
                toast.success('Profilo aggiornato.', { theme: 'colored' });
                LoginManager.notifySessionChanged();
                reload();
            }
            else {
                toast.error(messaggioErrore(res, 'Salvataggio non riuscito.'), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setSaving(false);
        }
    }

    async function cambiaPassword(e) {
        e.preventDefault();
        if (!password.attuale || !password.nuova) {
            toast.error('Inserisci la password attuale e quella nuova.', { theme: 'colored' });
            return;
        }
        if (password.nuova !== password.conferma) {
            toast.error('La nuova password e la conferma non coincidono.', { theme: 'colored' });
            return;
        }

        setSavingPassword(true);
        try {
            const res = await LoginManager.changePassword(
                password.attuale, password.nuova, password.conferma
            );

            if (res && res.success === 1) {
                toast.success('Password aggiornata.', { theme: 'colored' });
                setPassword({ attuale: '', nuova: '', conferma: '' });
            }
            else {
                // il backend risponde in inglese: qui interessa solo il caso
                // che l'utente puo' correggere da solo
                const codice = res && res.error_code;
                toast.error(
                    codice === 'profile.old_password'
                        ? 'La password attuale non e’ corretta.'
                        : messaggioErrore(res, 'Cambio password non riuscito.'),
                    { theme: 'colored' }
                );
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setSavingPassword(false);
        }
    }

    const stato = statoLista({ loading: loading, error: error, empty: !form, icon: 'user' });

    if (stato) {
        return (
            <main className="lv-shell lv-stack">
                <section className="lv-card lv-card-pad">
                    <SectionHead eyebrow="Area personale" title="Profilo utente" />
                    {stato}
                </section>
            </main>
        );
    }

    const nomeCompleto = nomeUtente(item);
    const haPalazzina = refId(item.idPalazzina) > 0;
    const haAppartamento = refId(item.idAppartamento) > 0;
    const haCasa = haPalazzina || haAppartamento;

    return (
        <main className="lv-shell lv-stack">
            <section className="lv-card lv-card-pad">
                <div className="lv-profile-hero">
                    <span className="lv-profile-avatar" aria-hidden="true">
                        {iniziali(nomeCompleto)}
                    </span>

                    <div className="lv-profile-ident">
                        <p className="lv-eyebrow">Area personale</p>
                        <h2 className="lv-title lv-title-lg">{nomeCompleto}</h2>
                        <p className="lv-profile-mail">{item.email}</p>
                    </div>

                    <div className="lv-profile-meta">
                        {haPalazzina && (
                            <div>
                                <span className="lv-stat-label">Palazzina</span>
                                <span className="lv-stat-value">
                                    {refLabel(item.idPalazzina, ['nome'])}
                                </span>
                            </div>
                        )}
                        {haAppartamento && (
                            <div>
                                <span className="lv-stat-label">Appartamento</span>
                                <span className="lv-stat-value">
                                    {refLabel(item.idAppartamento, ['identificativo'])}
                                </span>
                            </div>
                        )}
                        <div>
                            <span className="lv-stat-label">Ruolo</span>
                            <span className="lv-stat-value">{etichettaLivello(item.level)}</span>
                        </div>
                    </div>
                </div>

                {/* Senza casa la nota non puo' parlare di casa: resterebbe a
                    rimandare a un amministratore per qualcosa che non si vede. */}
                <p className="lv-profile-nota">
                    {haCasa ? 'Casa e ruolo li stabilisce' : 'Il ruolo lo stabilisce'} l’amministratore
                    del condominio: se qualcosa non torna, segnalalo a lui.
                </p>
            </section>

            <div className="lv-profile-cols">
                <section className="lv-card lv-card-pad">
                    <SectionHead
                        eyebrow="Recapiti"
                        title="I tuoi dati"
                        subtitle="Servono all'amministratore per raggiungerti sulle prenotazioni."
                    />

                    <form onSubmit={salvaDati}>
                        <div className="lv-form-grid lv-form-grid-compact">
                            <Field label="Nome" required>
                                <input
                                    className="lv-input"
                                    value={form.name}
                                    onChange={e => aggiorna('name', e.target.value)}
                                />
                            </Field>
                            <Field label="Cognome" required>
                                <input
                                    className="lv-input"
                                    value={form.surname}
                                    onChange={e => aggiorna('surname', e.target.value)}
                                />
                            </Field>
                            <Field label="Email" required hint="Con questa accedi alla piattaforma.">
                                <input
                                    type="email"
                                    className="lv-input"
                                    value={form.email}
                                    onChange={e => aggiorna('email', e.target.value)}
                                />
                            </Field>
                            <Field label="Telefono">
                                <input
                                    className="lv-input"
                                    value={form.telefono}
                                    onChange={e => aggiorna('telefono', e.target.value)}
                                />
                            </Field>
                        </div>

                        <DetailActions saving={saving} submitLabel="Salva modifiche" />
                    </form>
                </section>

                <section className="lv-card lv-card-pad">
                    <SectionHead
                        eyebrow="Sicurezza"
                        title="Password"
                        subtitle="Per cambiarla serve quella attuale."
                    />

                    {/* colonna stretta: i tre campi stanno uno sotto l'altro */}
                    <form onSubmit={cambiaPassword}>
                        <div className="lv-form-grid lv-form-grid-compact">
                            <Field label="Password attuale" required wide>
                                <input
                                    type="password"
                                    className="lv-input"
                                    autoComplete="current-password"
                                    value={password.attuale}
                                    onChange={e => aggiornaPassword('attuale', e.target.value)}
                                />
                            </Field>
                            <Field label="Nuova password" required wide>
                                <input
                                    type="password"
                                    className="lv-input"
                                    autoComplete="new-password"
                                    value={password.nuova}
                                    onChange={e => aggiornaPassword('nuova', e.target.value)}
                                />
                            </Field>
                            <Field label="Conferma nuova password" required wide>
                                <input
                                    type="password"
                                    className="lv-input"
                                    autoComplete="new-password"
                                    value={password.conferma}
                                    onChange={e => aggiornaPassword('conferma', e.target.value)}
                                />
                            </Field>
                        </div>

                        <DetailActions saving={savingPassword} submitLabel="Cambia password" />
                    </form>
                </section>
            </div>
        </main>
    );
}
